import React, { useMemo, useState } from "react";
import { PlusIcon, CheckIcon } from "@heroicons/react/24/outline";
import { format, isSameWeek, isToday } from "date-fns";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "../../../dialog/ConfirmDialog";
import OctagonAlertIcon from "../../../icons/OctagonAlertIcon";
import GroupedSectionHeader from "../../../components/ui/GroupedSectionHeader";
import HoverActionsMenu from "../../../components/HoverActionsMenu";

export type TaskRow = {
  id: string | number;
  title: string;
  createdAt?: number; // seconds epoch (used for grouping fallback)
  dueAt?: number; // seconds epoch (used for due label/color)
  dueLabel?: string; // '@Due today', '@Due in 2 days', '@Due 5 days ago'
  overdue?: boolean;
  assignees?: { name?: string; avatarUrl?: string }[];
  completed?: boolean;
};

export interface TasksSectionProps {
  title?: string;
  tasks: TaskRow[];
  onAddTask?: () => void;
  onOpen?: (id: string | number) => void;
  onToggleComplete?: (id: string | number) => void;
  onDelete?: (id: string | number) => void;
}

function groupByTime(tasks: TaskRow[]) {
  // Separate completed and incomplete tasks
  const completed: TaskRow[] = [];
  const incomplete: TaskRow[] = [];

  tasks.forEach((t) => {
    if (t.completed) {
      completed.push(t);
    } else {
      incomplete.push(t);
    }
  });

  // Sort incomplete tasks by createdAt (newest first)
  const sortedIncomplete = [...incomplete].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  // Sort completed tasks by createdAt (newest first)
  const sortedCompleted = [...completed].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  const today: TaskRow[] = [];
  const thisWeek: TaskRow[] = [];
  const months: Record<string, TaskRow[]> = {};
  const now = new Date();
  const weekOptions = { weekStartsOn: 1 as const };

  // Group incomplete tasks by time
  sortedIncomplete.forEach((t) => {
    const dt = t.createdAt ? new Date(t.createdAt * 1000) : undefined;
    if (!dt) {
      thisWeek.push(t);
      return;
    }
    if (isToday(dt)) {
      today.push(t);
      return;
    }
    if (isSameWeek(dt, now, weekOptions)) {
      thisWeek.push(t);
      return;
    }
    const label = format(dt, "MMMM yyyy");
    (months[label] ||= []).push(t);
  });

  return { today, thisWeek, months, completed: sortedCompleted };
}

function AssigneeAvatars({ list = [] as TaskRow["assignees"] }) {
  if (!list || list.length === 0) return null;
  const displayList = list.slice(0, 2);
  const remainingCount = list.length > 2 ? list.length - 2 : 0;

  return (
    <div className="flex items-center gap-1.5 min-w-0">
      {displayList.map((a, idx) => (
        <div key={idx} className="flex items-center gap-1.5 min-w-0">
          {a?.avatarUrl ? (
            <img
              src={a.avatarUrl}
              alt={a.name || "avatar"}
              className="w-5 h-5 rounded-full shrink-0"
            />
          ) : (
            <div className="w-5 h-5 rounded-full bg-gray-200 shrink-0" />
          )}
          {a?.name && <span className="text-xs truncate">{a.name}</span>}
        </div>
      ))}
      {remainingCount > 0 && <span className="text-xs text-gray-500">+{remainingCount}</span>}
    </div>
  );
}

export function TaskRowItem({
  t,
  onOpen,
  onToggleComplete,
  onDelete,
}: {
  t: TaskRow;
  onOpen?: (id: string | number) => void;
  onToggleComplete?: (id: string | number) => void;
  onDelete?: (id: string | number) => void;
}) {
  return (
    <div className="relative group flex items-center justify-start gap-2 py-2 hover:bg-gray-50 transition-colors rounded-md">
      {/* Checkbox */}
      <button
        type="button"
        onClick={() => onToggleComplete?.(t.id)}
        className={`w-4 h-4 rounded-sm border flex items-center justify-center ${
          t.completed ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"
        }`}
        aria-pressed={!!t.completed}
      >
        {t.completed && <CheckIcon className="w-3 h-3 text-white" />}
      </button>

      {/* Title */}
      <button
        type="button"
        onClick={() => onOpen?.(t.id)}
        className="flex-1 truncate text-left text-sm font-medium text-gray-900"
        title={t.title}
      >
        {t.title}
      </button>

      {/* Due label */}
      {t.dueLabel && (
        <div className={`flex-1 text-xs ${t.overdue ? "text-red-500" : "text-blue-500"}`}>
          {t.dueLabel}
        </div>
      )}

      {/* Assignees */}
      <div className="flex-2">
        <AssigneeAvatars list={t.assignees} />
      </div>

      {/* Hover Actions Menu */}
      <HoverActionsMenu
        onOpen={onOpen ? () => onOpen(t.id) : undefined}
        onDelete={onDelete ? () => onDelete(t.id) : undefined}
      />
    </div>
  );
}

function GroupBlock({
  label,
  count,
  isCollapsed,
  onToggle,
  children,
}: {
  label: string;
  count: number;
  isCollapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="w-full">
      <GroupedSectionHeader
        label={label}
        count={count}
        isCollapsed={isCollapsed}
        onToggle={onToggle}
      />
      {!isCollapsed && <div className="mt-1">{children}</div>}
    </div>
  );
}

export default function TasksSection({
  title = "Tasks",
  tasks,
  onAddTask,
  onOpen,
  onToggleComplete,
  onDelete,
}: TasksSectionProps) {
  const { t } = useTranslation();
  const groups = useMemo(() => groupByTime(tasks), [tasks]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const handleDeleteClick = (id: string | number) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId || !onDelete) return;
    setDeleting(true);
    try {
      await onDelete(deleteId);
      setConfirmOpen(false);
      setDeleteId(null);
    } catch {
      // Error handling if needed
    } finally {
      setDeleting(false);
    }
  };

  const handleGroupToggle = (label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  return (
    <div className="w-full flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between pb-2">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <button
          type="button"
          onClick={onAddTask}
          className="inline-flex items-center gap-1.5 px-2 py-1.5 bg-white border border-gray-300 rounded-md shadow-xs hover:bg-gray-50 text-xs font-medium text-gray-900"
        >
          <PlusIcon className="w-4 h-4" />
          Add Task
        </button>
      </div>

      {/* Completed tasks group */}
      {groups.completed.length > 0 && (
        <GroupBlock
          label="Completed"
          count={groups.completed.length}
          isCollapsed={collapsedGroups.has("Completed")}
          onToggle={() => handleGroupToggle("Completed")}
        >
          {groups.completed.map((t) => (
            <TaskRowItem
              key={t.id}
              t={t}
              onOpen={onOpen}
              onToggleComplete={onToggleComplete}
              onDelete={handleDeleteClick}
            />
          ))}
        </GroupBlock>
      )}

      {/* Incomplete tasks grouped by time */}
      {groups.today.length > 0 && (
        <GroupBlock
          label="Today"
          count={groups.today.length}
          isCollapsed={collapsedGroups.has("Today")}
          onToggle={() => handleGroupToggle("Today")}
        >
          {groups.today.map((t) => (
            <TaskRowItem
              key={t.id}
              t={t}
              onOpen={onOpen}
              onToggleComplete={onToggleComplete}
              onDelete={handleDeleteClick}
            />
          ))}
        </GroupBlock>
      )}

      {groups.thisWeek.length > 0 && (
        <GroupBlock
          label="This week"
          count={groups.thisWeek.length}
          isCollapsed={collapsedGroups.has("This week")}
          onToggle={() => handleGroupToggle("This week")}
        >
          {groups.thisWeek.map((t) => (
            <TaskRowItem
              key={t.id}
              t={t}
              onOpen={onOpen}
              onToggleComplete={onToggleComplete}
              onDelete={handleDeleteClick}
            />
          ))}
        </GroupBlock>
      )}

      {Object.entries(groups.months).map(([label, list]) => (
        <GroupBlock
          key={label}
          label={label}
          count={list.length}
          isCollapsed={collapsedGroups.has(label)}
          onToggle={() => handleGroupToggle(label)}
        >
          {list.map((t) => (
            <TaskRowItem
              key={t.id}
              t={t}
              onOpen={onOpen}
              onToggleComplete={onToggleComplete}
              onDelete={handleDeleteClick}
            />
          ))}
        </GroupBlock>
      ))}

      {tasks.length === 0 && (
        <div className="py-8 text-center text-sm text-gray-600">No tasks yet</div>
      )}

      <ConfirmDialog
        visible={confirmOpen}
        title={t("Delete task?")}
        description={t("This action cannot be undone. The task will be permanently deleted.")}
        alertIcon={<OctagonAlertIcon />}
        dangerBtn
        submitting={deleting}
        onClose={() => {
          if (!deleting) {
            setConfirmOpen(false);
            setDeleteId(null);
          }
        }}
        onSubmit={handleConfirmDelete}
        submitBtnLabel={t("Delete")}
      />
    </div>
  );
}
